/*
 *                     NOTICE
 *            COPYRIGHT 2025 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { calcParamNames } from "abweb/service/router/RouteParser";
import { URLUtils } from "abweb/utils/URLUtils";

// Finds curly bracket fragment that doesn't contain a curly bracket
export const NESTED_CURLY_BRACKETS_REGEX = /{([^{}]*)}/;
export function calcRouteWithParameters(routeTemplate, routeParams, extraParams) {
  // Approach:
  // 1. Process all nested optional blocks, with 'deepest' nested optional block first.
  //    If a nested optional block has unresolved param names, remove the entire optional block from the route.
  // 2. Process the remaining params in the resulting required block.  If any params are missing, throw an
  //    error.

  // ABSDK-5069: encoded param values can have '(', ')' characters within them as the
  // following chars are NOT escaped by encodeURIComponent: A-Z a-z 0-9 - _ . ! ~ * ' ( )
  // As such, replace routeTemplate parentheses (denoting optional route fragments) with curly brackets as curly brackets are never
  // in encoded param values (param values would have escaped curly brackets).  This ensures that innermost regex
  // does not match against parens in param values!
  let route = routeTemplate.replaceAll("(", "{").replaceAll(")", "}");
  // 1. Nested optional group processing.  Processes innermost optional block first (don't have any optional blocks within them),
  // substituting (including dropping optional frags altogether if they have unresolved param values)
  let match = route.match(NESTED_CURLY_BRACKETS_REGEX);
  while (match) {
    const parensRouteTemplate = match[1]; // first match group already has parentheses stripped off
    const parensRoute = paramsSubstitute(parensRouteTemplate, routeParams, false, routeTemplate);
    route = route.replace(match[0], parensRoute); // Note parensRoute can be empty string
    match = route.match(NESTED_CURLY_BRACKETS_REGEX);
  }
  // 2. Process remaining required params in route
  route = paramsSubstitute(route, routeParams, true, routeTemplate);

  // 3. Handle extra params
  route = calcExtraParams(route, extraParams);
  return route;
}

/**
 * Return route fragment with parameters injected into template.  If there are paramNames where there is no
 * corresponding param value in the routeParams, then throw if it is a required route fragment (throwOnMissingParam is true)
 * or return empty string if it is an optional route fragment.
 * @param routeTemplateFragment
 * @param routeParams
 * @param throwOnMissingParam
 * @param fullRouteTemplate
 */
function paramsSubstitute(routeTemplateFragment, routeParams, throwOnMissingParam, fullRouteTemplate) {
  const paramNames = calcParamNames(routeTemplateFragment);
  let route = routeTemplateFragment;
  for (const paramName of paramNames) {
    const rawParamVal = routeParams[paramName];
    if (rawParamVal != null) {
      // encode the param value appropriately based on type
      const paramVal = URLUtils.calcFragmentParamValue(paramName, rawParamVal, fullRouteTemplate);
      // Do not want :foo to match against :foobar in route, so use negative lookahead
      const paramRegex = new RegExp("\\:" + paramName + "(?!\\w)", "g");
      route = route.replaceAll(paramRegex, paramVal);
      // route = route.replaceAll(/\:${paramName}"\\:" + paramName + "(?!\\w)", paramVal);
      continue;
    }
    // ELSE missing param.  If throwOnMissingParam (required params) throw
    if (throwOnMissingParam) {
      throw new Error(`Missing required parameter '${paramName}' required in routeTemplate='${fullRouteTemplate}'`);
    }
    // Else it is an optional param missing, so drop the entire optional fragment by returning empty string
    return "";
  }
  return route;
}
function calcExtraParams(retRoute, extraParams) {
  if (extraParams == null || Object.keys(extraParams).length === 0) {
    return retRoute;
  }
  // Defensively handle objectToQueryString returning empty string.  Suppress emitting ? in this case
  const extraQS = URLUtils.objectToQueryString(extraParams);
  return extraQS ? `${retRoute}?${extraQS}` : retRoute;
}
//# sourceMappingURL=RouteEmitter.js.map
